import React, { useState, useEffect, useCallback } from 'react';
import { lndAPI, clientSettingsAPI, userAPI } from '../../../services';
import {
  GraduationCap, Plus, BookOpen, Users, CheckCircle, BarChart3,
  Edit2, Trash2, X, Send, Shield, RefreshCw, Search, Upload, Loader2
} from 'lucide-react';

const CATEGORIES = ['technical','leadership','compliance','soft_skills','safety','onboarding','sales','hr_policy','other'];
const CAT_LABELS = { technical:'Technical', leadership:'Leadership', compliance:'Compliance', soft_skills:'Soft Skills', safety:'Safety', onboarding:'Onboarding', sales:'Sales', hr_policy:'HR Policy', other:'Other' };
const ASSIGN_TYPES = [{ v:'all', l:'All Employees' },{ v:'department', l:'Department Wise' },{ v:'designation', l:'Designation Wise' },{ v:'individual', l:'Individual Employee' }];

const EMPTY_COURSE = {
  title:'', description:'', category:'other', durationHours:1, courseType:'optional',
  assignmentType:'all', targetDepartments:[], targetDesignations:[], targetEmployees:[],
  lessons:[{ title:'', content:'', videoUrl:'', order:0, knowledgeCheck:{ question:'', options:['','','',''], correctAnswer:0, explanation:'' } }],
  assessment:{ questions:[{ question:'', options:['','','',''], correctAnswer:0 }], passingPercentage:70 }
};

const LndPage = () => {
  const [tab, setTab] = useState('courses');
  const [courses, setCourses] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editCourse, setEditCourse] = useState(null);
  const [form, setForm] = useState(EMPTY_COURSE);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [empSearch, setEmpSearch] = useState('');
  const [uploadingLesson, setUploadingLesson] = useState(null);

  const showToast = (msg, type='success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [cr, comp] = await Promise.all([lndAPI.getCourses(), lndAPI.getComplianceDashboard()]);
      setCourses(Array.isArray(cr?.data) ? cr.data : []);
      setCompliance(comp?.data || null);
    } catch { showToast('Failed to load', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadOrgData = async () => {
    try {
      const [deptRes, desigRes, empRes] = await Promise.all([
        clientSettingsAPI.getDepartments(),
        clientSettingsAPI.getDesignations(),
        userAPI.getUsers({ limit: 200, active: true })
      ]);
      setDepartments(Array.isArray(deptRes?.data) ? deptRes.data : []);
      setDesignations(Array.isArray(desigRes?.data) ? desigRes.data : []);
      const empArr = empRes?.data?.users || empRes?.data || [];
      setEmployees(Array.isArray(empArr) ? empArr : []);
    } catch { /* ignore */ }
  };

  const openCreate = () => { setForm(EMPTY_COURSE); setEditCourse(null); setEmpSearch(''); setShowForm(true); loadOrgData(); };
  const openEdit = (c) => { setForm({ ...c }); setEditCourse(c._id); setEmpSearch(''); setShowForm(true); loadOrgData(); };

  const handleSave = async () => {
    if (!form.title.trim()) return showToast('Title required', 'error');
    try {
      setSaving(true);
      if (editCourse) await lndAPI.updateCourse(editCourse, form);
      else await lndAPI.createCourse(form);
      showToast(editCourse ? 'Course updated' : 'Course created');
      setShowForm(false); load();
    } catch { showToast('Save failed', 'error'); }
    finally { setSaving(false); }
  };

  const handlePublish = async (id) => {
    try { await lndAPI.publishCourse(id); showToast('Course published'); load(); }
    catch { showToast('Publish failed', 'error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this course?')) return;
    try { await lndAPI.deleteCourse(id); showToast('Deleted'); load(); }
    catch { showToast('Delete failed', 'error'); }
  };

  const addLesson = () => {
    setForm(f => ({ ...f, lessons: [...f.lessons, { title:'', content:'', videoUrl:'', order: f.lessons.length, knowledgeCheck:{ question:'', options:['','','',''], correctAnswer:0, explanation:'' } }] }));
  };

  const updateLesson = (i, field, val) => {
    setForm(f => { const l = [...f.lessons]; l[i] = { ...l[i], [field]: val }; return { ...f, lessons: l }; });
  };

  const updateKC = (li, field, val) => {
    setForm(f => { const l = [...f.lessons]; l[li].knowledgeCheck = { ...l[li].knowledgeCheck, [field]: val }; return { ...f, lessons: l }; });
  };

  const handleVideoUpload = async (li, file) => {
    if (!file) return;
    try {
      setUploadingLesson(li);
      const res = await lndAPI.uploadLessonVideo(file);
      if (res?.data?.url) updateLesson(li, 'videoUrl', res.data.url);
      else showToast('Upload failed', 'error');
    } catch { showToast('Video upload failed', 'error'); }
    finally { setUploadingLesson(null); }
  };

  const addAssessmentQ = () => {
    setForm(f => ({ ...f, assessment: { ...f.assessment, questions: [...f.assessment.questions, { question:'', options:['','','',''], correctAnswer:0 }] } }));
  };

  const updateAssessmentQ = (i, field, val) => {
    setForm(f => { const q = [...f.assessment.questions]; q[i] = { ...q[i], [field]: val }; return { ...f, assessment: { ...f.assessment, questions: q } }; });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {toast && <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${toast.type==='error'?'bg-red-500':'bg-green-500'}`}>{toast.msg}</div>}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><GraduationCap className="w-6 h-6 text-blue-600"/>Learning & Development</h1>
          <p className="text-gray-500 text-sm mt-1">Manage courses, track compliance, monitor employee learning</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"><RefreshCw className="w-4 h-4"/></button>
          <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"><Plus className="w-4 h-4"/>New Course</button>
        </div>
      </div>

      {/* Compliance Stats */}
      {compliance && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            { label:'Compliance', value:`${compliance.compliancePct}%`, icon:Shield, color:'text-blue-600', bg:'bg-blue-50' },
            { label:'Completed', value:compliance.completed, icon:CheckCircle, color:'text-green-600', bg:'bg-green-50' },
            { label:'In Progress', value:compliance.inProgress, icon:BookOpen, color:'text-yellow-600', bg:'bg-yellow-50' },
            { label:'Not Started', value:compliance.notStarted, icon:Users, color:'text-red-600', bg:'bg-red-50' },
          ].map(({ label, value, icon: Icon, color, bg }) => (
            <div key={label} className={`${bg} rounded-xl p-4 flex items-center gap-3`}>
              <Icon className={`w-8 h-8 ${color}`}/>
              <div><p className="text-xs text-gray-500">{label}</p><p className={`text-lg font-bold ${color}`}>{value}</p></div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-lg p-1 w-fit">
        {[['courses','Courses'],['compliance','Compliance']].map(([key, lbl]) => (
          <button key={key} onClick={() => setTab(key)} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab===key?'bg-white shadow text-blue-600':'text-gray-600 hover:text-gray-900'}`}>{lbl}</button>
        ))}
      </div>

      {tab === 'courses' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? <p className="col-span-3 text-center py-10 text-gray-400">Loading...</p>
          : courses.length === 0 ? (
            <div className="col-span-3 flex flex-col items-center py-16 text-gray-400 bg-white rounded-xl border border-gray-200">
              <GraduationCap className="w-12 h-12 mb-3 opacity-30"/>
              <p>No courses yet. Create your first course.</p>
            </div>
          ) : courses.map(c => (
            <div key={c._id} className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col gap-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-gray-900">{c.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{CAT_LABELS[c.category]} · {c.durationHours}h · {c.lessons?.length || 0} lessons</p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${c.courseType==='mandatory'?'bg-red-100 text-red-700':'bg-gray-100 text-gray-600'}`}>{c.courseType==='mandatory'?'Mandatory':'Optional'}</span>
              </div>
              {c.description && <p className="text-xs text-gray-500 line-clamp-2">{c.description}</p>}
              <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
                <span className={`px-2 py-0.5 rounded-full text-xs ${c.isPublished?'bg-green-100 text-green-700':'bg-yellow-100 text-yellow-700'}`}>{c.isPublished?'Published':'Draft'}</span>
                <div className="flex gap-1 ml-auto">
                  {!c.isPublished && <button onClick={() => handlePublish(c._id)} className="p-1.5 text-green-600 hover:bg-green-50 rounded" title="Publish"><Send className="w-4 h-4"/></button>}
                  <button onClick={() => openEdit(c)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"><Edit2 className="w-4 h-4"/></button>
                  <button onClick={() => handleDelete(c._id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4"/></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'compliance' && compliance && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-blue-600"/><span className="font-semibold text-gray-900">Department Compliance</span></div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>{['Department','Completed','Total','Compliance %'].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {compliance.departments?.map(d => (
                <tr key={d.name} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{d.name}</td>
                  <td className="px-4 py-3 text-green-700 font-semibold">{d.completed}</td>
                  <td className="px-4 py-3 text-gray-600">{d.total}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2"><div className={`h-2 rounded-full ${d.pct>=80?'bg-green-500':d.pct>=50?'bg-yellow-500':'bg-red-500'}`} style={{ width:`${d.pct}%` }}/></div>
                      <span className="font-semibold text-xs w-10">{d.pct}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Course Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold text-gray-900">{editCourse?'Edit Course':'Create Course'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-5 space-y-5">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Course Title *</label>
                  <input value={form.title} onChange={e => setForm(f=>({...f,title:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" placeholder="Enter course title"/>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea value={form.description} onChange={e => setForm(f=>({...f,description:e.target.value}))} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"/>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select value={form.category} onChange={e => setForm(f=>({...f,category:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    {CATEGORIES.map(c => <option key={c} value={c}>{CAT_LABELS[c]}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (hours)</label>
                  <input type="number" min="0.5" step="0.5" value={form.durationHours} onChange={e => setForm(f=>({...f,durationHours:parseFloat(e.target.value)}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"/>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Course Type</label>
                  <select value={form.courseType} onChange={e => setForm(f=>({...f,courseType:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="mandatory">Mandatory</option>
                    <option value="optional">Optional</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                  <select
                    value={form.assignmentType}
                    onChange={e => setForm(f=>({...f, assignmentType:e.target.value, targetDepartments:[], targetDesignations:[], targetEmployees:[]}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    {ASSIGN_TYPES.map(a => <option key={a.v} value={a.v}>{a.l}</option>)}
                  </select>
                </div>

                {/* Department multi-select */}
                {form.assignmentType === 'department' && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Departments <span className="text-gray-400 font-normal">({form.targetDepartments?.length || 0} selected)</span>
                    </label>
                    {departments.length === 0 ? (
                      <p className="text-xs text-gray-400 italic">No departments configured in org settings.</p>
                    ) : (
                      <div className="border border-gray-300 rounded-lg p-2 max-h-40 overflow-y-auto bg-white space-y-1">
                        {departments.map(d => {
                          const name = d.name || d;
                          const checked = form.targetDepartments?.includes(name);
                          return (
                            <label key={name} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!checked}
                                onChange={() => setForm(f => ({
                                  ...f,
                                  targetDepartments: checked
                                    ? f.targetDepartments.filter(x => x !== name)
                                    : [...(f.targetDepartments || []), name]
                                }))}
                                className="w-3.5 h-3.5 text-blue-600 rounded"
                              />
                              <span className="text-sm text-gray-800">{name}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                    {form.targetDepartments?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {form.targetDepartments.map(d => (
                          <span key={d} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center gap-1">
                            {d}
                            <button type="button" onClick={() => setForm(f => ({ ...f, targetDepartments: f.targetDepartments.filter(x => x !== d) }))}><X className="w-3 h-3"/></button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Designation multi-select */}
                {form.assignmentType === 'designation' && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Designations <span className="text-gray-400 font-normal">({form.targetDesignations?.length || 0} selected)</span>
                    </label>
                    {designations.length === 0 ? (
                      <p className="text-xs text-gray-400 italic">No designations configured in org settings.</p>
                    ) : (
                      <div className="border border-gray-300 rounded-lg p-2 max-h-40 overflow-y-auto bg-white space-y-1">
                        {designations.map(d => {
                          const name = d.name || d;
                          const checked = form.targetDesignations?.includes(name);
                          return (
                            <label key={name} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!checked}
                                onChange={() => setForm(f => ({
                                  ...f,
                                  targetDesignations: checked
                                    ? f.targetDesignations.filter(x => x !== name)
                                    : [...(f.targetDesignations || []), name]
                                }))}
                                className="w-3.5 h-3.5 text-blue-600 rounded"
                              />
                              <span className="text-sm text-gray-800">{name}</span>
                              {d.department && <span className="text-xs text-gray-400 ml-auto">{d.department}</span>}
                            </label>
                          );
                        })}
                      </div>
                    )}
                    {form.targetDesignations?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {form.targetDesignations.map(d => (
                          <span key={d} className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full flex items-center gap-1">
                            {d}
                            <button type="button" onClick={() => setForm(f => ({ ...f, targetDesignations: f.targetDesignations.filter(x => x !== d) }))}><X className="w-3 h-3"/></button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Individual employee multi-select */}
                {form.assignmentType === 'individual' && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Employees <span className="text-gray-400 font-normal">({form.targetEmployees?.length || 0} selected)</span>
                    </label>
                    <div className="relative mb-1.5">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"/>
                      <input
                        value={empSearch}
                        onChange={e => setEmpSearch(e.target.value)}
                        placeholder="Search by name or employee ID..."
                        className="w-full pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    {employees.length === 0 ? (
                      <p className="text-xs text-gray-400 italic">No employees found.</p>
                    ) : (
                      <div className="border border-gray-300 rounded-lg p-2 max-h-44 overflow-y-auto bg-white space-y-1">
                        {employees
                          .filter(e => {
                            const q = empSearch.toLowerCase();
                            return !q || `${e.firstName} ${e.lastName}`.toLowerCase().includes(q) || (e.employeeId || '').toLowerCase().includes(q);
                          })
                          .map(e => {
                            const checked = form.targetEmployees?.includes(e._id);
                            return (
                              <label key={e._id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={!!checked}
                                  onChange={() => setForm(f => ({
                                    ...f,
                                    targetEmployees: checked
                                      ? f.targetEmployees.filter(x => x !== e._id)
                                      : [...(f.targetEmployees || []), e._id]
                                  }))}
                                  className="w-3.5 h-3.5 text-blue-600 rounded"
                                />
                                <div className="flex-1 min-w-0">
                                  <span className="text-sm text-gray-800 font-medium">{e.firstName} {e.lastName}</span>
                                  <span className="text-xs text-gray-400 ml-2">{e.employeeId}</span>
                                </div>
                                {e.department && <span className="text-xs text-gray-400 truncate max-w-24">{e.department}</span>}
                              </label>
                            );
                          })}
                      </div>
                    )}
                    {form.targetEmployees?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {form.targetEmployees.map(id => {
                          const emp = employees.find(e => e._id === id);
                          return (
                            <span key={id} className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full flex items-center gap-1">
                              {emp ? `${emp.firstName} ${emp.lastName}` : id}
                              <button type="button" onClick={() => setForm(f => ({ ...f, targetEmployees: f.targetEmployees.filter(x => x !== id) }))}><X className="w-3 h-3"/></button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Lessons */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="font-semibold text-gray-800">Lessons</p>
                  <button onClick={addLesson} className="text-xs text-blue-600 flex items-center gap-1 hover:underline"><Plus className="w-3 h-3"/>Add Lesson</button>
                </div>
                {form.lessons?.map((lesson, li) => (
                  <div key={li} className="border border-gray-200 rounded-lg p-4 mb-3 bg-gray-50">
                    <p className="text-xs font-semibold text-gray-500 mb-2">Lesson {li+1}</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <input value={lesson.title} onChange={e => updateLesson(li,'title',e.target.value)} placeholder="Lesson title" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"/>
                      </div>
                      <div className="col-span-2">
                        <textarea value={lesson.content} onChange={e => updateLesson(li,'content',e.target.value)} placeholder="Lesson content / description" rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"/>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs font-medium text-gray-500 mb-1">Video (upload or paste URL)</label>
                        <div className="flex gap-2">
                          <input
                            value={lesson.videoUrl}
                            onChange={e => updateLesson(li, 'videoUrl', e.target.value)}
                            placeholder="Paste video URL or upload a file →"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                          <label className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium cursor-pointer border transition-colors ${uploadingLesson === li ? 'bg-gray-100 text-gray-400 border-gray-200' : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'}`}>
                            {uploadingLesson === li
                              ? <><Loader2 className="w-4 h-4 animate-spin"/>Uploading...</>
                              : <><Upload className="w-4 h-4"/>Upload</>
                            }
                            <input
                              type="file"
                              accept="video/mp4,video/webm,video/ogg,video/quicktime,video/x-msvideo"
                              className="hidden"
                              disabled={uploadingLesson !== null}
                              onChange={e => handleVideoUpload(li, e.target.files[0])}
                            />
                          </label>
                        </div>
                        {lesson.videoUrl && (
                          <p className="text-xs text-green-600 mt-1 truncate">✓ {lesson.videoUrl}</p>
                        )}
                      </div>
                    </div>
                    {/* Knowledge Check */}
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs font-semibold text-gray-500 mb-2">Knowledge Check</p>
                      <input value={lesson.knowledgeCheck?.question} onChange={e => updateKC(li,'question',e.target.value)} placeholder="Question" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2"/>
                      {lesson.knowledgeCheck?.options?.map((opt, oi) => (
                        <div key={oi} className="flex items-center gap-2 mb-1">
                          <input type="radio" name={`kc-${li}`} checked={lesson.knowledgeCheck.correctAnswer===oi} onChange={() => updateKC(li,'correctAnswer',oi)} className="w-3 h-3"/>
                          <input value={opt} onChange={e => { const opts=[...lesson.knowledgeCheck.options]; opts[oi]=e.target.value; updateKC(li,'options',opts); }} placeholder={`Option ${oi+1}`} className="flex-1 px-2 py-1 border border-gray-200 rounded text-xs"/>
                        </div>
                      ))}
                      <input value={lesson.knowledgeCheck?.explanation} onChange={e => updateKC(li,'explanation',e.target.value)} placeholder="Explanation for correct answer" className="w-full px-3 py-1.5 border border-gray-200 rounded text-xs mt-1"/>
                    </div>
                  </div>
                ))}
              </div>

              {/* Final Assessment */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="font-semibold text-gray-800">Final Assessment</p>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    Passing: <input type="number" min="1" max="100" value={form.assessment?.passingPercentage} onChange={e => setForm(f=>({...f,assessment:{...f.assessment,passingPercentage:parseInt(e.target.value)}}))} className="w-14 px-2 py-1 border border-gray-300 rounded text-xs"/>%
                  </div>
                </div>
                {form.assessment?.questions?.map((q, qi) => (
                  <div key={qi} className="border border-gray-200 rounded-lg p-4 mb-3 bg-gray-50">
                    <p className="text-xs font-semibold text-gray-500 mb-2">Question {qi+1}</p>
                    <input value={q.question} onChange={e => updateAssessmentQ(qi,'question',e.target.value)} placeholder="Question" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2"/>
                    {q.options?.map((opt, oi) => (
                      <div key={oi} className="flex items-center gap-2 mb-1">
                        <input type="radio" name={`aq-${qi}`} checked={q.correctAnswer===oi} onChange={() => updateAssessmentQ(qi,'correctAnswer',oi)} className="w-3 h-3"/>
                        <input value={opt} onChange={e => { const opts=[...q.options]; opts[oi]=e.target.value; updateAssessmentQ(qi,'options',opts); }} placeholder={`Option ${oi+1}`} className="flex-1 px-2 py-1 border border-gray-200 rounded text-xs"/>
                      </div>
                    ))}
                  </div>
                ))}
                <button onClick={addAssessmentQ} className="text-xs text-blue-600 flex items-center gap-1 hover:underline"><Plus className="w-3 h-3"/>Add Question</button>
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowForm(false)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
                <button onClick={handleSave} disabled={saving} className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60">{saving?'Saving...':'Save Course'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LndPage;
